import { DurationPicker } from '@salecycle/ui-component-library';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { maximumDelay } from '../../configs/sendConfig';
import { IDuration, IUnit, millisToDuration, unitToMillis } from 'core/ui/utils/unitMillis';

export interface IDelaySelectorProps {
  delayInMillis: number;
  sessionTimeout: number;
  onChange: (delayInMillis: number) => void;
}

type DurationUnitOptions = { label: string; value: IUnit }[];

const durationUnitOptions = (t: any): DurationUnitOptions => [
  { label: t('common:timeUnits.months'), value: 'months' },
  { label: t('common:timeUnits.weeks'), value: 'weeks' },
  { label: t('common:timeUnits.days'), value: 'days' },
  { label: t('common:timeUnits.hours'), value: 'hours' },
  { label: t('common:timeUnits.minutes'), value: 'minutes' },
];

export const DelaySelector: React.FC<IDelaySelectorProps> = props => {
  const { t } = useTranslation();
  const [interval, setInterval] = React.useState<IDuration>(millisToDuration(props.delayInMillis));

  const onIntervalChange = (newDelay: IDuration) => {
    if (unitToMillis(newDelay.value, newDelay.unit) < props.sessionTimeout) {
      setInterval(millisToDuration(props.sessionTimeout));
      props.onChange(unitToMillis(interval.value, interval.unit));
    } else if (unitToMillis(newDelay.value, newDelay.unit.toLocaleLowerCase() as IUnit) > maximumDelay) {
      setInterval(millisToDuration(maximumDelay));
      props.onChange(unitToMillis(interval.value, interval.unit));
    } else {
      setInterval(newDelay);
      props.onChange(unitToMillis(newDelay.value, newDelay.unit));
    }
  };

  const isInvalidDuration = () => {
    const result = isNotDefined() || isLessThanSessionTimeout() || exceedsMaximumDelay();
    if (!result) {
      props.onChange(unitToMillis(interval.value, interval.unit));
    }
    return result;
  };

  const isNotDefined = () => {
    return interval.value === undefined || isNaN(interval.value);
  };

  const isLessThanSessionTimeout = () => {
    return unitToMillis(interval.value, interval.unit) < props.sessionTimeout;
  };

  const exceedsMaximumDelay = (): boolean => {
    return unitToMillis(interval.value, interval.unit.toLocaleLowerCase() as IUnit) > maximumDelay;
  };

  const getDurationValidationMessage = (): string => {
    if (isNotDefined()) {
      return t('campaigns:offsiteEditor.segments.cycleModal.validationMessageRequired');
    }
    if (isLessThanSessionTimeout()) {
      return t('campaigns:offsiteEditor.segments.cycleModal.validationMessageMinimum');
    }
    if (exceedsMaximumDelay()) {
      return t('campaigns:offsiteEditor.segments.cycleModal.validationMessageMaximum', { howMany: 32 });
    }
    return '';
  };

  return (
    <DurationPicker
      tooltipHeading={t('campaigns:offsiteEditor.segments.cycleModal.input.ariaLabel')}
      tooltipDescription={t('campaigns:offsiteEditor.segments.cycleModal.tooltipDescription')}
      value={interval.value}
      onChange={(value, unit) => onIntervalChange({ value, unit } as IDuration)}
      label={t('campaigns:offsiteEditor.segments.cycleModal.input.ariaLabel')}
      unit={interval.unit}
      ariaLabel={t('campaigns:offsiteEditor.segments.cycleModal.durationPickerAriaLabel')}
      unitAriaLabel={t('campaigns:offsiteEditor.segments.cycleModal.durationPickerUnitAriaLabel')}
      units={durationUnitOptions(t)}
      validationState={isInvalidDuration() ? 'warning' : null}
      validationMessage={getDurationValidationMessage()}
    />
  );
};
